/** Row 1 maps to position 0, which the grid never renders. */
const MIN_PLACEMENT_ROW = 2;

/**
 * Maps a 1-indexed row to the 0-indexed position the grid inserts at. `unprefixedPodsPerRow`
 * scales the unprefixed index, where the grid injects 1-up but pods may render N-up.
 *
 * @param {object} params
 * @param {number} params.row - 1-indexed grid row.
 * @param {number} [params.unprefixedPodsPerRow=1] - visual pods per row at the unprefixed breakpoint.
 * @returns {{ unprefixed: number, sm: number, md: number, lg: number, xl: number }} per-breakpoint position.
 */
const rowToPosition = ({ row, unprefixedPodsPerRow = 1 }) => {
  const index = row - 1;
  return {
    unprefixed: index * unprefixedPodsPerRow, sm: index, md: index, lg: index, xl: index
  };
};

/**
 * Builds the slots for the given placements: one per componentName, for a registered componentName
 * with an integer row at or above MIN_PLACEMENT_ROW. A placement stacks on a row already held.
 *
 * An override moves an authored placement's row; it cannot add one. Every slot carries clampToEnd,
 * so the grid renders a row past the results after the last product.
 *
 * @param {object} params
 * @param {Array<{ componentName: string, row: number }>} [params.placements] - placements to render.
 * @param {Object<string, number>} [params.placementOverride={}] - componentName -> row that replaces
 *   the authored row for that componentName.
 * @param {Object<string, () => *>} [params.registry={}] - componentName -> factory returning the component.
 * @param {number} [params.unprefixedPodsPerRow=1] - visual pods per row at the unprefixed breakpoint.
 * @returns {Array<{ position: object, component: *, clampToEnd: boolean }>} the newly-added slots.
 */
export const getInGridPlacementSlots = ({
  placements,
  placementOverride = {},
  registry = {},
  unprefixedPodsPerRow = 1
} = {}) => {
  const placementList = (Array.isArray(placements) ? placements : []).map((placement) => {
    const row = placementOverride[placement?.componentName];
    return Number.isInteger(row) ? { ...placement, row } : placement;
  });
  const added = [];
  const slottedComponentNames = new Set();

  placementList.forEach((placement) => {
    const factory = registry[placement?.componentName];
    if (!factory || !Number.isInteger(placement?.row) || placement.row < MIN_PLACEMENT_ROW) {
      return;
    }

    if (slottedComponentNames.has(placement.componentName)) {
      return;
    }

    const position = rowToPosition({ row: placement.row, unprefixedPodsPerRow });
    const slot = { position, component: factory(), clampToEnd: true };
    slottedComponentNames.add(placement.componentName);
    added.push(slot);
  });

  return added;
};
